/* global google */
import React, { useEffect, useRef } from "react";
import { Map, useMap, useMapsLibrary } from "@vis.gl/react-google-maps";

const MapContainer = ({
  onPolygonChange,
  shapesDeleted,
  initialPolygonData = [],
}) => {
  const map = useMap();
  const drawing = useMapsLibrary("drawing");
  const places = useMapsLibrary("places");

  const polygonRef = useRef(null);
  const drawingManagerRef = useRef(null);
  const lastLoadedRef = useRef(null); // 🔥 track last update

  // ==================================================
  // 1️⃣ DRAWING MANAGER
  // ==================================================
  useEffect(() => {
    if (!map || !drawing) return;

    const manager = new drawing.DrawingManager({
      drawingMode: null,
      drawingControl: true,
      drawingControlOptions: {
        position: google.maps.ControlPosition.BOTTOM_RIGHT,
        drawingModes: [google.maps.drawing.OverlayType.POLYGON],
      },
      polygonOptions: {
        fillColor: "#ff0000",
        fillOpacity: 0.3,
        strokeColor: "#ff0000",
        strokeWeight: 2,
        editable: true,
        draggable: false,
      },
    });

    manager.setMap(map);
    drawingManagerRef.current = manager;

    google.maps.event.addListener(manager, "overlaycomplete", (e) => {
      if (polygonRef.current) {
        e.overlay.setMap(null);
        manager.setDrawingMode(null);
        return;
      }

      polygonRef.current = e.overlay;
      manager.setDrawingMode(null);

      attachPolygonListeners(e.overlay);
      emitPolygonChange(e.overlay);
    });

    return () => manager.setMap(null);
  }, [map, drawing]);

  // ==================================================
  // 2️⃣ LOAD / UPDATE POLYGON FROM PARENT
  // ==================================================
  useEffect(() => {
    if (!map) return;

    const serialized = JSON.stringify(initialPolygonData || []);

    // 🔥 prevent unnecessary reload
    if (serialized === lastLoadedRef.current) return;
    lastLoadedRef.current = serialized;

    // 🔥 clear old polygon
    if (polygonRef.current) {
      polygonRef.current.setMap(null);
      polygonRef.current = null;
    }

    if (!initialPolygonData.length) return;

    const polygon = new google.maps.Polygon({
      paths: initialPolygonData,
      map,
      editable: true,
      draggable: false,
      fillColor: "#ff0000",
      fillOpacity: 0.3,
      strokeColor: "#ff0000",
      strokeWeight: 2,
    });

    polygonRef.current = polygon;
    attachPolygonListeners(polygon);

    const bounds = new google.maps.LatLngBounds();
    initialPolygonData.forEach((p) => bounds.extend(p));
    map.fitBounds(bounds);
  }, [map, initialPolygonData]);

  // ==================================================
  // 3️⃣ POLYGON EVENTS
  // ==================================================
  const attachPolygonListeners = (polygon) => {
    const path = polygon.getPath();

    google.maps.event.addListener(path, "set_at", () =>
      emitPolygonChange(polygon)
    );
    google.maps.event.addListener(path, "insert_at", () =>
      emitPolygonChange(polygon)
    );

    google.maps.event.addListener(polygon, "mousedown", () => {
      map.setOptions({ draggable: false });
    });

    google.maps.event.addListener(polygon, "mouseup", () => {
      map.setOptions({ draggable: true });
    });
  };

  const emitPolygonChange = (polygon) => {
    const coords = polygon
      .getPath()
      .getArray()
      .map((p) => ({ lat: p.lat(), lng: p.lng() }));

    onPolygonChange(coords);
  };

  // ==================================================
  // 4️⃣ DELETE
  // ==================================================
  useEffect(() => {
    if (!shapesDeleted) return;

    if (polygonRef.current) {
      polygonRef.current.setMap(null);
      polygonRef.current = null;
      lastLoadedRef.current = null;
      onPolygonChange([]);
    }
  }, [shapesDeleted]);

  // ==================================================
  // 5️⃣ SEARCH
  // ==================================================
  useEffect(() => {
    if (!map || !places) return;

    const input = document.getElementById("searchBox");
    if (!input) return;

    const autocomplete = new places.Autocomplete(input);
    autocomplete.bindTo("bounds", map);

    autocomplete.addListener("place_changed", () => {
      const place = autocomplete.getPlace();
      if (!place.geometry) return;

      place.geometry.viewport
        ? map.fitBounds(place.geometry.viewport)
        : map.setCenter(place.geometry.location);
    });
  }, [map, places]);

  return (
    <div style={{ position: "relative" }}>
      <input
        id="searchBox"
        placeholder="Search Location"
        style={{
          position: "absolute",
          top: 10,
          right: 10,
          zIndex: 10,
          width: 250,
          height: 40,
          padding: "0 12px",
          borderRadius: "6px",
          border: "1px solid #ccc",
        }}
      />

      <Map
        defaultZoom={14}
        defaultCenter={{ lat: 11.1271, lng: 78.6569 }}
        style={{ width: "100%", height: "450px", borderRadius: "10px" }}
      />
    </div>
  );
};

export default MapContainer;
